import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment, { Moment } from 'moment';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { getAttioValuationDetail, getValuationStatusOptions } from 'dashboard409a/services/attio';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { ModelWithValuationData } from 'dashboard409a/services/models.types';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { getValuationStatusPaletteKey } from 'dashboard409a/utilities/getValuationStatusPaletteColor';

/**
 * Calculates a date that is N business days from today.
 * Business days exclude weekends (Saturday and Sunday).
 * @param businessDays - Number of business days to add (default: 4)
 * @returns Date string in YYYY-MM-DD format
 */
export const addBusinessDays = (date?: Date | Moment | string, days: number = 4): string => {
  let result = moment(date || new Date());
  let daysAdded = 0;

  while (daysAdded < days) {
    result = result.add(1, 'days');

    if (result.day() !== 0 && result.day() !== 6) {
      daysAdded += 1;
    }
  }

  return result.format('YYYY-MM-DD');
};

/**
 * Type guard to check if data is already a full AttioValuationDetailResponse
 */
export const isFullDetailResponse = (value: unknown): value is AttioValuationDetailResponse =>
  value !== null &&
  typeof value === 'object' &&
  'valuedEntity' in value &&
  'valuedInformation' in value &&
  'deal' in value

/**
 * Helper to parse currency string to number
 */
const parseCurrency = (value: string | null | undefined): number => {
  if (!value) {
    return 0;
  }
  const num = Number(value.replaceAll(/[^0-9.-]/g, ''));
  return Number.isNaN(num) ? 0 : num;
};

/**
 * Transform ModelWithValuationData to AttioValuationDetailResponse
 * Fields already available are populated; API-only fields use '' as loading sentinel
 */
export const transformModelToDetail = (
  model: ModelWithValuationData
): AttioValuationDetailResponse => ({
  valuedEntity: {
    valuedEntityId: model.attio_valuation_id,
    companyName: model.company_name || '-',
    primaryContact: '', // API-only – will be loaded from API
    primaryContactId: undefined,
    category: '', // API-only – will be loaded from API
    jobTitle: '', // API-only – will be loaded from API
    salesRep: '', // API-only – will be loaded from API
    salesRepImageSrc: '', // API-only – will be loaded from API
    companyUrl: undefined,
    valuedEntityNotice: undefined,
    primaryContactNotice: undefined,
  },
  valuedInformation: {
    valuationDate: model.valuation_date || '-',
    valuationName: model.company_name || '-',
    valuationType: '', // API-only – will be loaded from API
    bookingType: '', // API-only – will be loaded from API
    bookingDate: '-',
    valuationLeadName: model.valuation_lead || '',
    valuationLeadImageSrc: model.valuation_lead_image_src || '',
    valuationId: model.attio_valuation_id,
    renewalDate: '-',
    valuationUrl: undefined,
    otherConsultantName: '',
    otherConsultantImageSrc: '',
    otherConsultantEmail: '', // API-only – will be loaded from API
    valuationNotice: undefined,
    valuationFee: 0,
    valuationNetValue: '-',
    valuationEngagementManager: '',
    valuationEngagementManagerImageSrc: '',
    valuationEngagementManagerRole: '', // API-only – will be loaded from API
    split: 0,
    payment1: 0,
    payment1Date: undefined,
    payment2: 0,
    payment2Date: undefined,
    payment3: 0,
    payment3Date: undefined,
    refund: 0,
    refundDate: undefined,
    adjustment: 0,
    adjustmentDate: undefined,
    badDebt: 0,
    badDebtDate: undefined,
    renewalRate: 0,
    due: 0,
  },
  deal: {
    dealId: model.attio_valuation_id,
    valuationDate: model.valuation_date || '-',
    dealTotal: '-',
    dealStage: model.valuation_status || '-',
    due: '-',
    engagementManager: '',
    engagementManagerImageSrc: '',
    engagementManagerRole: '', // API-only – will be loaded from API
    dealUrl: undefined,
    dealName: '', // API-only – will be loaded from API
    dealNotice: undefined,
    contractStatus: undefined,
    signedPDF: undefined,
    elURL: undefined,
    net: undefined,
    dealDate: model.valuation_date || undefined,
    dealStageOptions: undefined,
  },
  status: model.valuation_status || '-',
  statusId: undefined,
});

/**
 * Transform SimplifiedAttioValuation to AttioValuationDetailResponse
 */
export const transformSimplifiedToDetail = (
  simplified: SimplifiedAttioValuation
): AttioValuationDetailResponse => ({
  valuedEntity: {
    valuedEntityId: simplified.record_id,
    companyName: simplified.company_name || '-',
    primaryContact: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    primaryContactId: undefined,
    jobTitle: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    salesRep: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    salesRepImageSrc: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    companyUrl: undefined,
    valuedEntityNotice: undefined,
    primaryContactNotice: undefined,
  },
  valuedInformation: {
    engagementCategory: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    valuationDate: simplified.valuation_date || '-',
    valuationName: simplified.company_name || '-',
    valuationType: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    bookingType: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    bookingDate: simplified.created_at || '-',
    valuationLeadName: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    valuationLeadImageSrc: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    valuationId: simplified.record_id,
    renewalDate: '-', // Not available in SimplifiedAttioValuation
    valuationUrl: undefined,
    otherConsultantName: (Array.isArray(simplified.other_consultant) ? simplified.other_consultant[0] : simplified.other_consultant) || '-',
    otherConsultantImageSrc: (Array.isArray(simplified.other_consultant_image_src) ? simplified.other_consultant_image_src[0] : simplified.other_consultant_image_src) || '-',
    otherConsultantEmail: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    otherConsultants: (() => {
      let names: string[];
      if (Array.isArray(simplified.other_consultant)) {
        names = simplified.other_consultant;
      } else if (simplified.other_consultant && simplified.other_consultant !== '-') {
        names = [simplified.other_consultant];
      } else {
        names = [];
      }
      let images: string[];
      if (Array.isArray(simplified.other_consultant_image_src)) {
        images = simplified.other_consultant_image_src;
      } else if (simplified.other_consultant_image_src && simplified.other_consultant_image_src !== '-') {
        images = [simplified.other_consultant_image_src];
      } else {
        images = [];
      }
      let ids: string[];
      if (Array.isArray(simplified.other_consultant_id)) {
        ids = simplified.other_consultant_id;
      } else if (simplified.other_consultant_id && simplified.other_consultant_id !== '-') {
        ids = [simplified.other_consultant_id];
      } else {
        ids = [];
      }
      return names
        .filter(Boolean)
        .map((name, i) => ({
          name,
          imageSrc: images[i] || '',
          targetRecordId: ids[i] || undefined,
        }));
    })(),
    otherConsultantId: (() => {
      if (Array.isArray(simplified.other_consultant_id)) {
        return simplified.other_consultant_id[0] || undefined;
      }
      return simplified.other_consultant_id && simplified.other_consultant_id !== '-'
        ? simplified.other_consultant_id
        : undefined;
    })(),
    otherConsultantIds: (() => {
      if (Array.isArray(simplified.other_consultant_id)) {
        return simplified.other_consultant_id.filter(id => Boolean(id) && id !== '-');
      }
      return simplified.other_consultant_id && simplified.other_consultant_id !== '-'
        ? [simplified.other_consultant_id]
        : undefined;
    })(),
    valuationNotice: undefined,
    valuationFee: parseCurrency(simplified.valuation_fee),
    valuationNetValue: simplified.net_value || '-',
    valuationEngagementManager: simplified.engagement_manager || '-',
    valuationEngagementManagerImageSrc: simplified.engagement_manager_image_src || '-',
    valuationEngagementManagerRole: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    // Financial fields
    split: parseCurrency(simplified.split),
    payment1: parseCurrency(simplified.payment_1),
    payment1Date: simplified.pay_1_date || undefined,
    payment2: parseCurrency(simplified.payment_2),
    payment2Date: simplified.pay_2_date || undefined,
    payment3: parseCurrency(simplified.payment_3),
    payment3Date: simplified.pay_3_date || undefined,
    refund: parseCurrency(simplified.refund),
    refundDate: simplified.refund_date || undefined,
    adjustment: parseCurrency(simplified.adjustment),
    adjustmentDate: simplified.adjustment_date || undefined,
    badDebt: parseCurrency(simplified.bad_debt),
    badDebtDate: simplified.bad_debt_date || undefined,
    renewalRate: parseCurrency(simplified.renewal_rate),
    due: parseCurrency(simplified.due),
  },
  deal: {
    dealId: simplified.deal_id || simplified.record_id, // Fallback to record_id if deal_id not available
    valuationDate: simplified.valuation_date || '-',
    dealTotal: simplified.valuation_fee || '-',
    dealStage: simplified.deal_stage || '-',
    due: simplified.deal_due || simplified.due || '-',
    engagementManager: simplified.engagement_manager || '-',
    engagementManagerImageSrc: simplified.engagement_manager_image_src || '-',
    engagementManagerRole: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    dealUrl: undefined,
    dealName: '', // Not available in SimplifiedAttioValuation – will be loaded from API
    dealNotice: undefined,
    contractStatus: undefined,
    signedPDF: undefined,
    elURL: undefined,
    net: simplified.net_value || undefined,
    dealDate: simplified.valuation_date || undefined,
    dealStageOptions: undefined,
  },
  status: simplified.status || '-',
  statusId: undefined, // Will need to be fetched from status options if needed
});

interface UseValuationDetailProps {
  data: ModelWithValuationData | SimplifiedAttioValuation | string | undefined;
  open: boolean;
}

/**
 * Hook to handle valuation detail data transformation and API calls
 * Provides progressive/lazy loading: shows transformed data immediately, then enhances with API data
 */
export const useValuationDetail = ({ data, open }: UseValuationDetailProps) => {
  // Check if data already contains full detail (has valuedEntity, valuedInformation, and deal)
  const hasFullDetail = useMemo(() => isFullDetailResponse(data), [data]);

  // Extract valuationId from either ModelWithValuationData, SimplifiedAttioValuation, or string
  const valuationId = useMemo(() => {
    if (!data) {
      return undefined;
    }
    if (typeof data === 'string') {
      return data;
    }
    // Check if it's ModelWithValuationData (has attio_valuation_id) or SimplifiedAttioValuation (has record_id)
    return 'attio_valuation_id' in data ? data.attio_valuation_id : data.record_id;
  }, [data]);

  // Check if data is SimplifiedAttioValuation (from valuations table row)
  const isSimplifiedValuation = useMemo(() => {
    if (!data || typeof data === 'string') {
      return false;
    }
    return 'record_id' in data && !('attio_valuation_id' in data) && !isFullDetailResponse(data);
  }, [data]);

  // Check if data is ModelWithValuationData (from models table row)
  const isModelData = useMemo(() => {
    if (!data || typeof data === 'string') {
      return false;
    }
    return 'attio_valuation_id' in data && !isFullDetailResponse(data);
  }, [data]);

  // Transform to AttioValuationDetailResponse if needed (for progressive loading)
  const transformedDetail = useMemo(() => {
    if (isSimplifiedValuation && data && typeof data !== 'string' && 'record_id' in data) {
      return transformSimplifiedToDetail(data);
    }
    if (isModelData && data && typeof data !== 'string' && 'attio_valuation_id' in data) {
      return transformModelToDetail(data);
    }
    return undefined;
  }, [isSimplifiedValuation, isModelData, data]);

  // Fetch status options from Attio API
  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Transform AttioStatusOption to StatusOption
  const statusOptions = useMemo(() => attioStatusOptions.map(option => ({
    id: option.id.option_id,
    label: option.title,
    isArchived: option.is_archived,
    paletteKey: getValuationStatusPaletteKey(option.title),
  })), [attioStatusOptions]);

  // Update transformedDetail with statusId if we have status options and the status matches
  const transformedDetailWithStatusId = useMemo(() => {
    if (!transformedDetail) {
      return transformedDetail;
    }
    // Try to find statusId from status options if status is available
    if (transformedDetail.status && statusOptions.length > 0) {
      const matchingStatus = statusOptions.find(option => option.label === transformedDetail.status);
      if (matchingStatus) {
        return {
          ...transformedDetail,
          statusId: matchingStatus.id,
        };
      }
    }
    return transformedDetail;
  }, [transformedDetail, statusOptions]);

  // Always make API call to get full detail, but show transformed data immediately
  // Skip API call only if we already have full detail structure
  const shouldSkipApiCall = useMemo(() => hasFullDetail, [hasFullDetail]);

  const {
    data: valuationDetailFromQuery,
    isError,
    error,
    isLoading,
    refetch: refetchValuationDetail,
  } = useQuery<AttioValuationDetailResponse, Error>({
    queryKey: [AttioQueryKeys.VALUATION_ITEM, valuationId],
    queryFn: () => getAttioValuationDetail(valuationId as string),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!valuationId && open && !shouldSkipApiCall, // Make API call unless we already have full detail
  });

  // Use transformed data immediately, then merge with API response when it arrives
  // This provides progressive/lazy loading: show what we have, enhance with API data
  const valuationDetail = useMemo(() => {
    // If we already have full detail, use it
    if (hasFullDetail && isFullDetailResponse(data)) {
      return data;
    }

    // If we have API response, use it (it's the most complete)
    if (valuationDetailFromQuery) {
      return valuationDetailFromQuery;
    }

    // Otherwise, use transformed data from table row (show immediately while API loads)
    if (transformedDetailWithStatusId) {
      return transformedDetailWithStatusId;
    }

    return undefined;
  }, [hasFullDetail, data, transformedDetailWithStatusId, valuationDetailFromQuery]);

  return {
    valuationDetail,
    valuationId,
    isLoading,
    isError,
    error,
    refetchValuationDetail,
    statusOptions,
    isLoadingStatusOptions,
  };
};

export const useOperationsModal = ({ models }: { models: ModelWithValuationData[] }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useMemo(() => queryString.parse(location.search), [location.search]);
  const modelIdFromUrl = queryParams['model-id'] as string | undefined;

  const [openModal, setOpenModal] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<number | undefined>();
  const [pinnedModel, setPinnedModel] = useState<ModelWithValuationData | undefined>();

  const selectedModel = useMemo(() => {
    if (!openModal) {
      return undefined;
    }
    const fresh =
      selectedModelId !== undefined
        ? models.find(model => model.id === selectedModelId || model.company_measurement_date === selectedModelId)
        : undefined;
    return fresh ?? pinnedModel ?? modelIdFromUrl ?? undefined;
  }, [openModal, modelIdFromUrl, models, pinnedModel, selectedModelId]);

  const handleOpenModal = useCallback((model: ModelWithValuationData) => {
    setPinnedModel(model);
    setSelectedModelId(model.id || model.company_measurement_date);
    setOpenModal(true);

    const newQueryParams = { ...queryParams, 'model-id': model.attio_valuation_id };
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate, queryParams]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedModelId(undefined);
    setPinnedModel(undefined);

    const newQueryParams = { ...queryParams };
    delete newQueryParams['model-id'];
    navigate({ search: queryString.stringify(newQueryParams) }, { replace: true });
  }, [navigate, queryParams]);

  // Auto-open from URL; hydrate row when present, otherwise load detail by Attio id alone (may be outside filters)
  useEffect(() => {
    if (!modelIdFromUrl) {
      return;
    }
    const model = models.find(m => m.attio_valuation_id === modelIdFromUrl);
    if (!openModal) {
      setOpenModal(true);
    }
    if (model) {
      setPinnedModel(model);
      setSelectedModelId(model.id || model.company_measurement_date);
    } else if (!openModal) {
      setPinnedModel(undefined);
      setSelectedModelId(undefined);
    }
  }, [modelIdFromUrl, models, openModal]);

  return {
    openModal,
    selectedModel,
    handleOpenModal,
    handleCloseModal,
  };
};

